import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import type { Tag, TagStatus } from '../../types/tag'
import { formatFullDateTime } from '../../utils/dateUtils'
import Modal from '../Modal'
import StatusBadge from '../StatusBadge'
import TagConfirmation from './TagConfirmation'

interface ViewTagModalProps {
  tag: Tag
  onClose: () => void
}

const ViewTagModal: React.FC<ViewTagModalProps> = ({ tag, onClose }) => {
  // Map Tag data to TagConfirmation expected format
  // Build assigned driver snapshot from Tag inline snapshot fields
  const assignedDriverSnapshot =
    tag.assigned_driver_first_name || tag.assigned_driver_last_name
      ? {
          first_name: tag.assigned_driver_first_name || '',
          last_name: tag.assigned_driver_last_name || '',
          email: tag.assigned_driver_email || '',
          phone: tag.assigned_driver_phone || undefined,
          company_name: tag.assigned_driver_company_name || undefined,
        }
      : undefined

  const tagConfirmationData = {
    title: tag.title || undefined,
    origin_name: tag.origin_name,
    origin_address: tag.origin_address,
    destination_address: tag.destination_address,
    material: tag.material || undefined,
    rate_type: tag.rate_type,
    pay_rate: tag.pay_rate,
    notes: tag.notes || undefined,
    start_date: tag.start_date,
    assigned_user_id:
      tag.assigned_user?.id || tag.assigned_to?.contact_object?.id,
    assigned_user: tag.assigned_user,
    shared_emails: [],
    contractor_user: tag.contractor_user,
    broker_user: tag.broker_user,
    project_label:
      tag.project && typeof tag.project === 'object'
        ? (tag.project as { id: number; label?: string }).label
        : undefined,
    workorder_label:
      tag.workorder && typeof tag.workorder === 'object'
        ? (tag.workorder as { id: number; label?: string }).label
        : undefined,
    tag_label: tag.label,
    // Snapshot data (immutable historical data)
    contractor_snapshot: tag.contractor_snapshot,
    broker_snapshot: tag.broker_snapshot,
    sub_hauler_snapshot: tag.sub_hauler_snapshot,
    dispatcher_snapshot: tag.dispatcher_snapshot,
    assigned_driver_snapshot: assignedDriverSnapshot,
    // Role of who created/sent the dispatch
    dispatch_created_by_role: tag.dispatch_created_by_role,
  }

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <button
            type="button"
            onClick={onClose}
            className={css(styles.closeButton)}
          >
            <i className="fa-solid fa-times" />
          </button>
          <div className={css(styles.titleRow)}>
            <h2 className={css(styles.title)}>
              {tag.status?.toUpperCase() === 'COMPLETE'
                ? 'Tag – Completed'
                : tag.status?.toUpperCase() === 'DECLINED'
                  ? 'Tag – Declined'
                  : 'Tag – Open'}
            </h2>
            <StatusBadge status={(tag.status || 'NEW') as TagStatus} />
          </div>
          <div className={css(styles.subHeaderRow)}>
            <div className={css(styles.tagId)}>TAG ID: {tag.label}</div>
            {tag.title && (
              <span className={css(styles.tagTitle)}>TITLE: {tag.title}</span>
            )}
          </div>
        </div>
        <div className={css(styles.content)}>
          <TagConfirmation data={tagConfirmationData} />

          {/* Decline Information Section */}
          {tag.status?.toUpperCase() === 'DECLINED' && tag.decline_reason && (
            <div className={css(styles.declineSection)}>
              <h4 className={css(styles.declineTitle)}>Decline Reason:</h4>
              <p className={css(styles.declineReason)}>{tag.decline_reason}</p>
              {tag.declined_by && (
                <p className={css(styles.declinedBy)}>
                  Declined by{' '}
                  {`${tag.declined_by.first_name} ${tag.declined_by.last_name}`.trim() ||
                    tag.declined_by.email}
                  {tag.declined_at &&
                    ` on ${formatFullDateTime(tag.declined_at)}`}
                </p>
              )}
            </div>
          )}
        </div>
        <div className={css(styles.footer)}>
          <button onClick={onClose} className={css(styles.closeBtn)}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  tagId: {
    fontSize: '12px',
    color: '#6B7280',
  },
  tagTitle: {
    fontSize: '12px',
    color: '#6B7280',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
    ':hover': {
      color: '#1F2937',
    },
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#4B5563',
    },
  },
  declineSection: {
    marginTop: '24px',
    backgroundColor: '#FFFBEB',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    padding: '16px',
  },
  declineTitle: {
    margin: 0,
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#92400E',
  },
  declineReason: {
    margin: 0,
    marginBottom: '12px',
    fontSize: '14px',
    color: '#78350F',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  declinedBy: {
    margin: 0,
    fontSize: '12px',
    color: '#B45309',
    fontStyle: 'italic',
  },
})

export default ViewTagModal
